import * as React from 'react';

import { cn } from '@/lib/utils';

// TABLE
const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className='tw-overflow-x-auto tw-border-y tw-relative tw-overflow-hidden' style={{borderColor: '#d8dee4'}}>
      <table
        ref={ref}
        className={cn('tw-w-full tw-caption-bottom tw-text-sm tw-table-auto', className)}
        {...props}
      />
    </div>
  ),
);
Table.displayName = 'Table';

//TABLE HEADER
const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => <thead ref={ref} {...props} />);
TableHeader.displayName = 'TableHeader';

//TABLE HEAD
const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'tw-h-7 tw-px-4 tw-text-xs fw-500 tw-td-th-shadow text-muted-foreground tw-whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className || undefined,
    )}
    
    {...props}
  />
));
TableHead.displayName = 'TableHead';

//TABLE BODY
const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => <tbody ref={ref} className={cn('', className)} {...props} />);
TableBody.displayName = 'TableBody';

//TABLE ROW
const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & { isInteractive?: boolean }
>(({ className, isInteractive, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      isInteractive
        ? 'tw-transition-colors hover:tw-bg-zinc-100/50 data-[state=selected]:tw-bg-zinc-100 dark:hover:tw-bg-zinc-800/50 dark:data-[state=selected]:tw-bg-zinc-800 tw-cursor-pointer'
        : '',
      className,
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

//TABLE CELL
const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'tw-align-middle tw-px-4 tw-whitespace-normal tw-whitespace-nowrap tw-td-th-shadow tw-py-[6.5px]',
      className,
    )}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

//TABLE CAPTION
const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('tw-mt-4 tw-text-sm tw-text-zinc-500 dark:tw-text-zinc-400', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

//TABLE FOOTER
const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'tw-border-t tw-bg-zinc-100/50 tw-font-medium [&>tr]:last:tw-border-b-0 dark:tw-bg-zinc-800/50',
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
